import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { NutriScore } from './NutriScore'
import { useGetFactScoreSummary, useSubmitFactScore } from '@/application/services/fact.service'
import type { ScoreLetter } from '@/domains/types'

interface FactScoreInlineProps {
  leadId: string
  factId: string
  /**
   * Management mode: show both validated + average with labels.
   * Default (false) = public mode.
   */
  showBoth?: boolean
  /**
   * Public mode only: when true and no validated score exists, the user
   * can click a grade to cast or update their vote.
   */
  allowVoting?: boolean
  /** Auto-refresh interval in ms. When set, the score summary is polled at this cadence. */
  refetchInterval?: number
}

/**
 * Fetches and displays the NutriScore badge(s) for a single fact.
 *
 * - Public mode (default, showBoth=false):
 *     • If a validated score exists → static NutriScore, no label.
 *     • If allowVoting=true and no validated score → interactive NutriScore
 *       with the user's own vote pre-selected (or none).
 *     • Otherwise → renders nothing.
 *
 * - Management mode (showBoth=true): validated + average, each with a label.
 */
export function FactScoreInline({
  leadId,
  factId,
  showBoth = false,
  allowVoting = false,
  refetchInterval,
}: FactScoreInlineProps) {
  const { t } = useTranslation()
  const { data: summary, refetch } = useGetFactScoreSummary(leadId, factId, { refetchInterval })
  const { mutateAsync: submitScore, isPending } = useSubmitFactScore(leadId, factId)

  const hasValidated = !!summary?.validatedScore
  const hasAverage = !!summary?.averageScore

  const handleVote = async (grade: ScoreLetter) => {
    if (isPending) return
    try {
      await submitScore(grade)
      await refetch()
    } catch {
      toast.error(t('lead_detail.fact_score_validate_error'))
    }
  }

  // ── Public mode ─────────────────────────────────────────────────────────────

  if (!showBoth) {
    if (hasValidated) {
      return <NutriScore grade={summary!.validatedScore} size="sm" />
    }
    if (allowVoting) {
      return (
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-muted-foreground leading-none">
            {summary?.userScore
              ? t('lead_detail.fact_score_your_vote')
              : t('lead_detail.fact_score_vote_prompt')}
          </span>
          <NutriScore
            grade={summary?.userScore ?? null}
            size="sm"
            onSelect={isPending ? undefined : handleVote}
          />
        </div>
      )
    }
    return null
  }

  // ── Management mode ─────────────────────────────────────────────────────────

  if (!hasValidated && !hasAverage) return null

  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      {hasValidated && (
        <div className="flex flex-col items-center gap-0.5">
          <NutriScore grade={summary!.validatedScore} size="sm" />
          <span className="text-[10px] text-muted-foreground leading-none">
            {t('lead_detail.fact_score_validated_label')}
          </span>
        </div>
      )}
      {hasAverage && (
        <div className="flex flex-col items-center gap-0.5">
          <NutriScore grade={summary!.averageScore} size="sm" />
          <span className="text-[10px] text-muted-foreground leading-none">
            {t('lead_detail.fact_score_average_label')}
            {summary!.count > 0 && (
              <> ({t('lead_detail.fact_score_voters', { count: summary!.count })})</>
            )}
          </span>
        </div>
      )}
    </div>
  )
}
